import { useSuspenseQuery } from "@apollo/client";
import { createEmbeddingContext, type Parameter } from "amazon-quicksight-embedding-sdk";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorCard } from "@/components/ErrorCard";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { getErrorMessage } from "@/lib/utils/errors";

interface QuicksightDashboardProps {
  reportId: string;
}

export const QuicksightDashboard: React.FC<QuicksightDashboardProps> = ({ reportId }) => {
  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => (
        <ErrorCard
          title="Dashboard Error"
          description={error?.message || "Failed to load QuickSight dashboard"}
          className="min-h-[60vh]"
        />
      )}
    >
      <Suspense fallback={<QuicksightDashboardSkeleton />}>
        <QuicksightDashboardContent reportId={reportId} />
      </Suspense>
    </ErrorBoundary>
  );
};

const QuicksightDashboardContent: React.FC<QuicksightDashboardProps> = ({ reportId }) => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const { activeBusinessUnitIdInt } = useActiveBusinessUnit();
  const [embedError, setEmbedError] = useState<Error | null>(null);

  const { data } = useSuspenseQuery(getQuicksightReportQuery, {
    variables: {
      businessUnit: activeBusinessUnitIdInt || -1,
      report: reportId,
    },
    fetchPolicy: "network-only",
  });

  const embedUrl = data.quicksightReport?.embedUrl;
  const embedParameters = data.quicksightReport?.parameters;

  if (!embedParameters) {
    throw new Error("Failed to get report. Missing parameters.");
  }

  if (!embedUrl) {
    throw new Error("Failed to get report. Missing url.");
  }

  useEffect(() => {
    const embedDashboard = async () => {
      if (!dashboardRef.current) {
        return;
      }

      try {
        const parameters = removeNothings(embedParameters);

        const quicksightParameters: Parameter[] = parameters
          .filter((param): param is { code: string; value: string } => Boolean(param.code && param.value))
          .map((param) => ({
            Name: param.code,
            Values: [param.value],
          }));

        const embeddingContext = await createEmbeddingContext();

        await embeddingContext.embedDashboard(
          {
            url: embedUrl,
            container: dashboardRef.current,
            height: "1000px",
            width: "100%",
          },
          {
            parameters: quicksightParameters,
          },
        );
      } catch (err) {
        setEmbedError(new Error(`Failed to embed QuickSight dashboard: ${getErrorMessage(err)}`));
      }
    };

    void embedDashboard();
  }, [data, embedParameters, embedUrl]);

  if (embedError) {
    return (
      <ErrorCard
        title="Dashboard Error"
        description={embedError.message}
        className="min-h-[60vh] flex items-center justify-center"
      />
    );
  }

  return <div ref={dashboardRef} className="w-full" />;
};

const QuicksightDashboardSkeleton = () => {
  return (
    <div className="w-full h-[70vh] bg-muted/30 rounded flex items-center justify-center">
      <div className="text-center space-y-4">
        <Skeleton className="h-8 w-8 rounded-full mx-auto" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
};

const getQuicksightReportQuery = graphql(`
  query GetQuicksightReport($businessUnit: Int!, $report: ID!) {
    quicksightReport(businessUnit: $businessUnit, report: $report) {
      embedUrl
      parameters {
        code
        value
      }
    }
  }
`);
